import { ButtonType, ButtonWidget, RadioButtonWithLabelGroupWidget,RadioLayoutType, TabStyles } from '@tableau/widgets';
import React = require('react');
import { edit } from '~src/app/sagas/spaces/ScheduleMenuSagas';
import { Messages } from '~src/codegen/Localize';
import { CommonStyles } from '~src/common/react/CommonStyles';
import { DeleteContentsAction } from '~src/content/services/DeleteContentsAction';
import { SetScheduleStatusActionService } from '~src/schedule/actions/SetScheduleStatusAction';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { cssClass } from '~src/style';
import { SchedulePermissionsService } from '~src/ts/services/SchedulePermissions';
import { ToasterService } from '~src/ts/services/ToasterService';

import { ScheduleAction } from '../types/ISchedule';
import { IScheduleInfo } from '../types/IScheduleInfo';
import { localizeScheduleAction } from '../utils/ScheduleUtils';

export interface ScheduleDetailsSubspaceProps {
  readonly schedule: IScheduleInfo;
  readonly navigateToAllSchedules: () => void;
  readonly reloadSpace: () => void;
}

const cssPrefix = 'ScheduleDetailsSubspace';

const tableStyle = cssClass(cssPrefix, 'table', {
  maxWidth: '768px',
  paddingRight: '18px',
  marginTop: '12px',
  maxHeight: '300px',
});

const rowStyle = {
  padding: '12px',
};

const labelStyle = cssClass(cssPrefix, 'label', {
  width: '1%',
  paddingLeft: '18px',
  textAlign: 'right',
  fontSize: '15px',
  fontWeight: 'normal',
  color: CommonStyles.Colors.Grey153,
  lineHeight: '23px',
  whiteSpace: 'nowrap',
}, rowStyle);

const detailsStyle = {
  width: '100%',
  paddingTop: '15px',
  lineHeight: '1.35em',
};

const detailsBorderStyle = {
  borderBottom: `1px solid ${TabStyles.Colors.F7Disabled}`,
};

const detailsStyleWithNoBorder = cssClass(cssPrefix, 'details-no-border', rowStyle, detailsStyle);

const detailsStyleWithBorder = cssClass(cssPrefix, 'details-border', rowStyle, detailsStyle, detailsBorderStyle);

const buttonCellStyleWithBorder = cssClass(cssPrefix, 'details-button-cell', detailsBorderStyle);

export namespace ScheduleDetailsSubspaceHelpers {
  export function updateStatus(enabled: boolean, schedule: IScheduleInfo, reloadSpace: () => void) {
    const result = enabled
      ? SetScheduleStatusActionService.enableSchedule(schedule)
      : SetScheduleStatusActionService.disableSchedule(schedule);
    result.then(() => reloadSpace());
  }

  export function editSchedule(schedule: IScheduleInfo, reloadSpace: () => void) {
    edit([schedule]).then(() => reloadSpace());
  }

  export function deleteSchedule(schedule: IScheduleInfo, navigateToAllSchedules: () => void) {
    const scheduleInfo = { ...schedule, contentType: 'schedule' } as unknown as IContentInfo;
    DeleteContentsAction.confirmDeleteContents([scheduleInfo])
      .then(() => {
        ToasterService.instance.markToastsAsDoNotDismissOnLocationChange();
        navigateToAllSchedules();
      });
  }
}

export function ScheduleDetailsSubspace(props: ScheduleDetailsSubspaceProps) {
  const statusButtonList = [{
    label: Messages.enabled(),
    testId: 'schedule-details-status-enabled',
    value: true,
  }, {
    label: Messages.disabled(),
    testId: 'schedule-details-status-disabled',
    value: false,
  }];
  const scheduleEnabled = !!props.schedule.runNextAt;
  const selectedButtonIndex = statusButtonList.findIndex(button => {
    return button.value === scheduleEnabled;
  });
  const enabledText = scheduleEnabled ? Messages.enabled() : Messages.disabled();
  return <table className={tableStyle}>
    <tbody>
      <tr>
        <td className={labelStyle}>
          {Messages.taskType()}
        </td>
        <td className={detailsStyleWithBorder} data-tb-test-id={'schedule-details-task-type'}>
          {localizeScheduleAction(props.schedule.scheduledAction as ScheduleAction)}
        </td>
        <td className={buttonCellStyleWithBorder} />
      </tr>
      <tr>
        <td className={labelStyle}>
          {Messages.status()}
        </td>
        <td className={detailsStyleWithBorder} data-tb-test-id={'schedule-details-status'}>
          {SchedulePermissionsService.canEditSchedule()
            ? <RadioButtonWithLabelGroupWidget
              ariaLabel={Messages.status()}
              buttonList={statusButtonList}
              checkedIndex={selectedButtonIndex}
              handleChange={(enabled: boolean) => ScheduleDetailsSubspaceHelpers.updateStatus(enabled, props.schedule, props.reloadSpace)}
              layout={RadioLayoutType.Horizontal}
            />
            : enabledText
          }
        </td>
        <td className={buttonCellStyleWithBorder} />
      </tr>
      <tr>
        <td className={labelStyle}>
          {Messages.defaultPriority()}
        </td>
        <td className={detailsStyleWithNoBorder} data-tb-test-id={'schedule-details-priority'}>
          {Messages.createScheduleFormPriorityDescriptionWithValue({ PRIORITY: String(props.schedule.priority) })}
        </td>
        <td className={detailsStyleWithNoBorder} data-tb-test-id={'schedule-details-edit-button-cell'}>
          {SchedulePermissionsService.canEditSchedule() &&
            <ButtonWidget
              buttonType={ButtonType.Outline}
              testId='settings-edit-details'
              onClick={() => ScheduleDetailsSubspaceHelpers.editSchedule(props.schedule, props.reloadSpace)}
            >
              {Messages.editScheduleSettings()}
            </ButtonWidget>
          }
        </td>
      </tr>
      <tr>
        <td className={labelStyle}>
          {Messages.serialization()}
        </td>
        <td className={detailsStyleWithNoBorder} data-tb-test-id={'schedule-details-priority'}>
          {props.schedule.parallel
            ? Messages.createScheduleFormParallelDescription()
            : Messages.createScheduleFormSerialDescription()
          }
        </td>
      </tr>
      <tr>
        <td className={labelStyle}>
          {Messages.frequency()}
        </td>
        <td className={detailsStyleWithBorder} data-tb-test-id={'schedule-details-description'}>
          {IScheduleInfo.getScheduleDescription(props.schedule)}
        </td>
        <td className={buttonCellStyleWithBorder} />
      </tr>
      {SchedulePermissionsService.canEditSchedule() && <tr>
        <td className={labelStyle} />
        <td className={detailsStyleWithNoBorder} />
        <td>
          <ButtonWidget
            buttonType={ButtonType.Destructive}
            testId='settings-action-delete'
            onClick={() => ScheduleDetailsSubspaceHelpers.deleteSchedule(props.schedule, props.navigateToAllSchedules)}
          >
            {Messages.deleteSchedule_action()}
          </ButtonWidget>
        </td>
      </tr>}
    </tbody>
  </table>;
}
